import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { SplashScreen } from '../components/splash/SplashScreen'

// Bar: 0.5s delay + 1.2s × 2 passes = 2.9s. +300ms pause before fade starts.
const MIN_SPLASH_MS = 3200
// Match SplashScreen exit: 400ms fade + 200ms delay = 600ms total
const EXIT_MS = 600

// The full splash is a first-visit/login moment, not a per-tab or per-refresh
// one — without this flag, opening any link in a new tab (e.g. an offer card
// from search) re-plays the entire ~3.2s animation from scratch on every tab.
// Subsequent loads skip straight to the app; individual pages' own skeleton
// states cover the loading gap instead (per FEATURE-CONVENTIONS.md).
const SPLASH_SEEN_KEY = 'swim:splashSeen'

function hasSeenSplash(): boolean {
  try {
    return localStorage.getItem(SPLASH_SEEN_KEY) === '1'
  } catch {
    return false
  }
}

function markSplashSeen(): void {
  try {
    localStorage.setItem(SPLASH_SEEN_KEY, '1')
  } catch {
    // localStorage unavailable (private mode, etc.) — splash will just replay
  }
}

function clearSplashSeen(): void {
  try {
    localStorage.removeItem(SPLASH_SEEN_KEY)
  } catch {
    // no-op — nothing to clear
  }
}

interface SplashContextValue {
  splashDone: boolean
  /** Call on logout so the splash plays again on next login */
  resetSplash: () => void
  /**
   * Queue a callback to run after the splash finishes.
   * If the splash is already done, the callback runs immediately.
   * Use for notifications that should not appear while the splash is covering the screen.
   */
  queuePostSplash: (fn: () => void) => void
  /**
   * Called by SessionReadySignal when the session query settles (success or error).
   * Splash will not hide until BOTH the timer fires AND session is ready —
   * prevents the app rendering with an indeterminate auth state.
   */
  markSessionReady: () => void
}

const SplashContext = createContext<SplashContextValue>({
  splashDone: false,
  resetSplash: () => {},
  queuePostSplash: (fn) => fn(),
  markSessionReady: () => {},
})

export function useSplash() {
  return useContext(SplashContext)
}

interface SplashProviderProps {
  children: ReactNode
}

export function SplashProvider({ children }: SplashProviderProps) {
  const alreadySeen = useRef(hasSeenSplash())

  // Splash hides only when BOTH the timer fires AND session has settled —
  // both start pre-satisfied when the splash has already played once, so
  // the app renders immediately with no fullscreen overlay at all.
  const [timerDone, setTimerDone]       = useState(alreadySeen.current)
  const [sessionReady, setSessionReady] = useState(alreadySeen.current)
  const splashDone = timerDone && sessionReady

  // fading = CSS exit transition playing; mounted = element in DOM
  const [fading, setFading]   = useState(false)
  const [mounted, setMounted] = useState(!alreadySeen.current)

  const queueRef = useRef<Array<() => void>>([])
  const splashDoneRef = useRef(false)
  splashDoneRef.current = splashDone

  // Drain queue when splashDone flips to true
  const prevSplashDone = useRef(alreadySeen.current)
  useEffect(() => {
    if (splashDone && !prevSplashDone.current) {
      prevSplashDone.current = true
      markSplashSeen()
      // Start exit fade, then unmount after transition completes
      setFading(true)
      const t = setTimeout(() => setMounted(false), EXIT_MS)
      queueRef.current.forEach(fn => fn())
      queueRef.current = []
      return () => clearTimeout(t)
    }
  }, [splashDone])

  useEffect(() => {
    if (alreadySeen.current) return // nothing to time — splash isn't showing
    const timer = setTimeout(() => setTimerDone(true), MIN_SPLASH_MS)
    return () => clearTimeout(timer)
  }, [])

  const resetSplash = useCallback(() => {
    clearSplashSeen()
    alreadySeen.current = false
    setTimerDone(false)
    setSessionReady(false)
    setFading(false)
    setMounted(true)
    prevSplashDone.current = false
    splashDoneRef.current = false
  }, [])

  const markSessionReady = useCallback(() => setSessionReady(true), [])

  const queuePostSplash = useCallback((fn: () => void) => {
    if (splashDoneRef.current) {
      fn()
    } else {
      queueRef.current.push(fn)
    }
  }, [])

  return (
    <SplashContext.Provider value={{ splashDone, resetSplash, queuePostSplash, markSessionReady }}>
      {mounted && <SplashScreen fading={fading} />}
      <div className={mounted && !fading ? 'invisible' : undefined}>{children}</div>
    </SplashContext.Provider>
  )
}
